import * as React from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Grid,
  Card,
  CardContent,
  CardActions,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Send,
  CheckCircle,
  Schedule,
  Group,
  Person,
  Message,
} from '@mui/icons-material';
import { Booking } from '@/types';
import { useSendManualReminder } from '@/hooks/api/useGuests';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { CommunicationHistoryPanel } from '@/components/common/CommunicationHistoryPanel';
import { BulkCommunicationDialog } from '@/components/common/BulkCommunicationDialog';

interface BookingCommunicationPanelProps {
  booking: Booking;
  listingTitle?: string;
}

interface ManualReminderForm {
  recipientType: 'all' | 'pending' | 'specific';
  specificGuestId?: string;
  channel: 'email' | 'whatsapp' | 'both';
  subject?: string;
  message: string;
}



export const BookingCommunicationPanel: React.FC<BookingCommunicationPanelProps> = ({
  booking,
  listingTitle,
}) => {
  const [manualReminderOpen, setManualReminderOpen] = React.useState(false);
  const [bulkCommunicationOpen, setBulkCommunicationOpen] = React.useState(false);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [manualReminderForm, setManualReminderForm] = React.useState<ManualReminderForm>({
    recipientType: 'pending',
    channel: 'both',
    message: '',
  });


  // API hooks
  const sendReminderMutation = useSendManualReminder();

  const handleSendManualReminder = async () => {
    try {
      await sendReminderMutation.mutateAsync({
        bookingId: booking.bookingId,
        recipientType: manualReminderForm.recipientType,
        specificGuestId: manualReminderForm.specificGuestId,
        channel: manualReminderForm.channel,
        subject: manualReminderForm.subject,
        message: manualReminderForm.message,
      });

      setManualReminderOpen(false);
      setManualReminderForm({
        recipientType: 'pending',
        channel: 'both',
        message: '',
      });

      // Refresh communication history by updating the refresh key
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Failed to send manual reminder:', error);
    }
  };





  // Get guest completion status for display
  const guestStatus = booking.guestCheckInStatus || { completed: 0, pending: 0 };
  const totalGuests = booking.expectedGuests || booking.guests || 0;
  
  // Calculate actual pending count: total guests minus completed
  const actualPending = Math.max(0, totalGuests - guestStatus.completed);
  const actualCompleted = guestStatus.completed;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Quick Actions */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Send color="primary" />
          Quick Communication Actions
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Schedule color="warning" />
                  <Typography variant="subtitle1">
                    Check-in Reminders
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Send reminders to guests who haven't completed check-in
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Chip
                    label={`${actualPending} pending`}
                    color="warning"
                    size="small"
                    icon={<Person fontSize="small" />}
                  />
                  <Chip
                    label={`${actualCompleted} completed`}
                    color="success"
                    size="small"
                    icon={<CheckCircle fontSize="small" />}
                  />
                </Box>
              </CardContent>
              <CardActions>
                <Button
                  variant="outlined"
                  startIcon={<Send />}
                  onClick={() => setManualReminderOpen(true)}
                  disabled={actualPending === 0}
                  size="small"
                >
                  Send Reminder
                </Button>
              </CardActions>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Group color="primary" />
                  <Typography variant="subtitle1">
                    Bulk Communication
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Send custom messages to all guests for this booking
                </Typography>
                <Chip
                  label={`${totalGuests} total guests`}
                  color="primary"
                  size="small"
                  icon={<Group fontSize="small" />}
                />
              </CardContent>
              <CardActions>
                <Button
                  variant="outlined"
                  startIcon={<Message />}
                  onClick={() => setBulkCommunicationOpen(true)}
                  size="small"
                >
                  Send Message
                </Button>
              </CardActions>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Communication History */}
      <CommunicationHistoryPanel
        key={refreshKey}
        bookingId={booking.bookingId}
        title="Communication History"
        showFilters={true}
        showActions={false}
        maxHeight={500}
      />

      {/* Manual Reminder Dialog */}
      <Dialog open={manualReminderOpen} onClose={() => setManualReminderOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Send Manual Reminder</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Recipients</InputLabel>
              <Select
                value={manualReminderForm.recipientType}
                label="Recipients"
                onChange={(e) => setManualReminderForm(prev => ({
                  ...prev,
                  recipientType: e.target.value as any
                }))}
              >
                <MenuItem value="all">All Guests</MenuItem>
                <MenuItem value="pending">Pending Check-in Only</MenuItem>
                <MenuItem value="specific">Specific Guest</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Communication Channel</InputLabel>
              <Select
                value={manualReminderForm.channel}
                label="Communication Channel"
                onChange={(e) => setManualReminderForm(prev => ({
                  ...prev,
                  channel: e.target.value as any
                }))}
              >
                <MenuItem value="both">Email + WhatsApp</MenuItem>
                <MenuItem value="email">Email Only</MenuItem>
                <MenuItem value="whatsapp">WhatsApp Only</MenuItem>
              </Select>
            </FormControl>

            {(manualReminderForm.channel === 'email' || manualReminderForm.channel === 'both') && (
              <TextField
                label="Email Subject"
                value={manualReminderForm.subject || ''}
                onChange={(e) => setManualReminderForm(prev => ({
                  ...prev,
                  subject: e.target.value
                }))}
                fullWidth
                placeholder="Check-in reminder for your upcoming stay"
              />
            )}

            <TextField
              label="Message"
              value={manualReminderForm.message}
              onChange={(e) => setManualReminderForm(prev => ({
                ...prev,
                message: e.target.value
              }))}
              multiline
              rows={4}
              fullWidth
              required
              placeholder="Please complete your check-in form before your arrival..."
            />

            <Alert severity="info">
              This reminder will be sent immediately to the selected recipients.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualReminderOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSendManualReminder}
            variant="contained"
            disabled={!manualReminderForm.message.trim() || sendReminderMutation.isLoading}
          >
            {sendReminderMutation.isLoading ? 'Sending...' : 'Send Reminder'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Communication Dialog */}
      <BulkCommunicationDialog
        open={bulkCommunicationOpen}
        onClose={() => setBulkCommunicationOpen(false)}
        booking={booking}
        listingTitle={listingTitle}
      />
    </Box>
  );
};

export default BookingCommunicationPanel;